'use client'

import Link from 'next/link'
import { useState, useRef } from 'react'
import TurnstileWidget, { TurnstileWidgetRef } from '@/components/TurnstileWidget'
import dynamic from 'next/dynamic'

const TurnstileWrapper = dynamic(() => import('@/components/TurnstileWrapper'), {
  ssr: false,
  loading: () => <div className="h-[65px] flex items-center justify-center text-sm text-gray-500">Loading security check...</div>
})

export default function ContactPageClient({ locale }: { locale: string }) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null
    message: string
  }>({ type: null, message: '' })
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const turnstileRef = useRef<TurnstileWidgetRef>(null)
  const [useFallback, setUseFallback] = useState(false)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (!turnstileToken) {
      setSubmitStatus({
        type: 'error',
        message: locale === 'de'
          ? 'Bitte bestätigen Sie, dass Sie kein Bot sind.'
          : 'Please verify that you are not a bot.'
      })
      return
    }

    setIsSubmitting(true)
    setSubmitStatus({ type: null, message: '' })

    const formData = new FormData(e.currentTarget)
    const data = {
      name: formData.get('name'),
      email: formData.get('email'),
      subject: formData.get('subject'),
      message: formData.get('message'),
      locale: locale,
      turnstileToken: turnstileToken
    }

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })

      const result = await response.json()

      if (response.ok) {
        setSubmitStatus({
          type: 'success',
          message: result.message || (locale === 'de'
            ? 'Ihre Nachricht wurde erfolgreich gesendet!'
            : 'Your message has been sent successfully!')
        })
        ;(e.target as HTMLFormElement).reset()
        turnstileRef.current?.reset()
        setTurnstileToken(null)
      } else {
        setSubmitStatus({
          type: 'error',
          message: locale === 'de'
            ? 'Fehler beim Senden der Nachricht. Bitte versuchen Sie es später erneut.'
            : 'Error sending message. Please try again later.'
        })
      }
    } catch (error) {
      console.error('Error:', error)
      setSubmitStatus({
        type: 'error',
        message: locale === 'de'
          ? 'Fehler beim Senden der Nachricht. Bitte versuchen Sie es später erneut.'
          : 'Error sending message. Please try again later.'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-white dark:bg-secondary-900">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-primary-900/10" />
        <div className="container relative">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-center mb-6">
            {locale === 'de' ? 'Kontakt' : 'Contact'}
          </h1>
          <p className="text-xl text-center text-secondary-600 dark:text-secondary-400 max-w-2xl mx-auto">
            {locale === 'de'
              ? 'Haben Sie Fragen oder möchten Sie mehr über TOBG erfahren? Kontaktieren Sie uns!'
              : 'Do you have questions or would like to learn more about TOBG? Contact us!'}
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              {locale === 'de' ? 'Kontaktformular' : 'Contact Form'}
            </h2>
            <form onSubmit={handleSubmit} className="bg-white dark:bg-secondary-800 p-8 rounded-lg shadow-lg">
              {submitStatus.type && (
                <div className={`mb-6 p-4 rounded-lg ${
                  submitStatus.type === 'success'
                    ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                    : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                }`}>
                  {submitStatus.message}
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium mb-2">
                    {locale === 'de' ? 'Name' : 'Name'}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-secondary-900 dark:text-white disabled:opacity-50"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    disabled={isSubmitting}
                    className="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-secondary-900 dark:text-white disabled:opacity-50"
                  />
                </div>
              </div>
              <div className="mb-6">
                <label htmlFor="subject" className="block text-sm font-medium mb-2">
                  {locale === 'de' ? 'Betreff' : 'Subject'}
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-secondary-900 dark:text-white disabled:opacity-50"
                />
              </div>
              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-medium mb-2">
                  {locale === 'de' ? 'Nachricht' : 'Message'}
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={6}
                  required
                  disabled={isSubmitting}
                  className="w-full px-4 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg focus:ring-2 focus:ring-primary-500 dark:bg-secondary-900 dark:text-white disabled:opacity-50"
                ></textarea>
              </div>

              {useFallback ? (
                <TurnstileWrapper
                  siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || ''}
                  onVerify={setTurnstileToken}
                  onError={(error) => {
                    console.error('Turnstile wrapper error:', error)
                  }}
                  theme="auto"
                  locale={locale}
                />
              ) : (
                <TurnstileWidget
                  ref={turnstileRef}
                  onVerify={setTurnstileToken}
                  onExpire={() => {
                    setTurnstileToken(null)
                    console.log('Turnstile token expired')
                  }}
                  onError={(error) => {
                    console.error('Turnstile error:', error)
                    if (error?.message?.includes('Maximum verification attempts')) {
                      setUseFallback(true)
                    }
                  }}
                  locale={locale}
                  className="mb-6"
                />
              )}

              <button
                type="submit"
                disabled={isSubmitting || !turnstileToken}
                className="w-full px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting
                  ? (locale === 'de' ? 'Wird gesendet...' : 'Sending...')
                  : (locale === 'de' ? 'Nachricht senden' : 'Send Message')}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 bg-secondary-50 dark:bg-secondary-800">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-12 max-w-4xl mx-auto">
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-bold mb-6">
                {locale === 'de' ? 'Kontaktinformationen' : 'Contact Information'}
              </h2>

              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold mb-2">
                    {locale === 'de' ? 'Adresse' : 'Address'}
                  </h3>
                  <p className="text-secondary-600 dark:text-secondary-400">
                    TOBG Services Ltd.<br />
                    Patrik Germann<br />
                    {locale === 'de' ? 'Vereinigtes Königreich' : 'United Kingdom'}
                  </p>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Email</h3>
                  <a href="mailto:hostmaster@tobg.ltd"
                     className="text-primary-600 dark:text-primary-400 hover:underline">
                    hostmaster@tobg.ltd
                  </a>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">
                    {locale === 'de' ? 'Terminbuchung' : 'Appointment Booking'}
                  </h3>
                  <p className="text-secondary-600 dark:text-secondary-400 mb-4">
                    {locale === 'de'
                      ? 'Buchen Sie einen persönlichen Beratungstermin über Timify.'
                      : 'Book a personal consultation appointment via Timify.'}
                  </p>
                  <Link
                    href="https://book.timify.com/?accountId=5c865d3ad151a021d040e152&hideCloseButton=true"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    {locale === 'de' ? 'Termin buchen' : 'Book appointment'}
                  </Link>
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div>
              <h2 className="text-2xl font-bold mb-6">
                {locale === 'de' ? 'Soziale Medien' : 'Social Media'}
              </h2>

              <div className="space-y-4">
                <Link href="https://www.linkedin.com/in/patrik-germann-aipuna/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-3 p-4 bg-secondary-50 dark:bg-secondary-800 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors">
                  <span className="text-lg font-medium">LinkedIn</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
